import React, { useState, useEffect } from 'react';
import { Box, Collapse, TextField, Typography, Divider, Button } from '@mui/material';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useOrganization } from '../contexts/OrganizationContext';
import { getSubscriptionByGuid } from '../services/subscriptionService';
import { CAPABILITIES, hasCapability } from '../services/capabilitiesService';

const PostflightSection = ({ missionData, handleFieldChange }) => {
    let primaryColor = '#2c3e50';
    const [isExpanded, setIsExpanded] = useState(true);
    const [isEditingFlightTime, setIsEditingFlightTime] = useState(false);
    const [editedFlightTime, setEditedFlightTime] = useState(null);
    const { selectedOrg } = useOrganization();
    const [subscription, setSubscription] = useState(null);

    useEffect(() => {
        const loadSubscriptionData = async () => {
            if (selectedOrg) {
                try {
                    const subscriptionData = await getSubscriptionByGuid(selectedOrg);
                    if (subscriptionData) {
                        setSubscription(subscriptionData.subscription);
                    } else {
                        throw new Error('Subscription not found');
                    }
                } catch (error) {
                    console.error('Error loading subscription data:', error);
                    setError('Failed to load subscription data.');
                }
            } else {
                setSubscription(null); // Clear subscription if no subscriptionGuid exists
            }
        };

        loadSubscriptionData();
    }, [selectedOrg]);

    // Calculate flight time (if available)
    const flightTime = missionData?.startedAt && missionData?.completedAt
        ? Math.max(0, Math.round(
            (new Date(missionData.completedAt) - new Date(missionData.startedAt)) / 60000 // convert ms to minutes
        ))
        : null;

    // Format dates
    const formatDate = (dateString) => {
        if (!dateString) {
            console.debug('formatDate: dateString is null or undefined', { dateString });
            return 'N/A';
        }

        try {
            const parsedDate = new Date(dateString);
            if (isNaN(parsedDate)) {
                console.debug('formatDate: Invalid date string', { dateString });
                return 'N/A';
            }

            return parsedDate.toLocaleString();
        } catch (error) {
            console.error('formatDate: Error while parsing date', { dateString, error });
            return 'N/A';
        }
    };

    const handleFlightTimeSave = () => {
        if (editedFlightTime === null || editedFlightTime < 0 || !missionData?.startedAt) {
            setIsEditingFlightTime(false);
            return;
        }

        // Calculate the new completedAt datetime
        const startedAt = new Date(missionData.startedAt);
        const newCompletedAt = new Date(startedAt.getTime() + editedFlightTime * 60000).toISOString();

        // Update the missionData fields
        handleFieldChange({ target: { name: 'actualFlightTime', value: editedFlightTime } });
        handleFieldChange({ target: { name: 'completedAt', value: newCompletedAt } });

        setIsEditingFlightTime(false);
    };

    return (
        <Box
            sx={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                backgroundColor: '#fafafa',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
        >
            {/* Section Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: isExpanded ? '16px' : '0px',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <FlightLandIcon sx={{ color: '#42a5f5', marginRight: '8px' }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Post-flight Actions
                </Typography>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            {/* Expandable Content */}
            <Collapse in={isExpanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                    {/* Static Metadata Section */}
                    <Box
                        sx={{
                            marginBottom: '16px',
                            padding: '16px',
                            borderRadius: '8px',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'left',
                            textAlign: 'left',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        {/* Mission Completed Message */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <VerifiedIcon sx={{ color: '#4caf50', fontSize: '24px' }} />
                            <Typography variant="h6" sx={{ fontWeight: '500px', color: '#4caf50' }}>
                                This mission has been completed
                            </Typography>
                        </Box>

                        {/* Divider */}
                        <Divider sx={{ width: '100%', marginBottom: '16px' }} />

                        {/* Static Metadata Section */}
                        <Typography variant="body1" gutterBottom>
                            <span style={{ fontWeight: 500 }}>Date Completed:</span>&nbsp;&nbsp;{formatDate(missionData?.completedAt)}
                        </Typography>
                        <Typography variant="body1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontWeight: 500 }}>Actual Flight Time:</span>&nbsp;&nbsp;{isEditingFlightTime ? (
                                <TextField
                                    type="number"
                                    size="small"
                                    value={editedFlightTime !== null ? editedFlightTime : flightTime || ''}
                                    onChange={(e) => setEditedFlightTime(Math.max(1, e.target.value))} // Ensure minimum value of 1
                                    sx={{ marginLeft: '8px', width: '100px' }}
                                />
                            ) : (
                                `${flightTime !== null ? `${flightTime} min` : 'N/A'}`
                            )}

                            {hasCapability(subscription?.planId, CAPABILITIES.POST_FLIGHT) ? (
                                <>
                                    {!isEditingFlightTime && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            sx={{
                                                marginLeft: '8px',
                                                textTransform: 'capitalize',
                                                backgroundColor: primaryColor,
                                                color: 'white',
                                                height: '30px', // Reduced height
                                                '&:hover': {
                                                    backgroundColor: '#1e3040', // Slightly darker shade for hover
                                                },
                                                padding: '4px 12px', // Adjust padding if needed
                                            }}
                                            onClick={() => {
                                                setIsEditingFlightTime(true);
                                                setEditedFlightTime(flightTime); // Set current value for editing
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {isEditingFlightTime && (
                                        <Box sx={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    backgroundColor: primaryColor,
                                                    color: 'white',
                                                    height: '30px', // Reduced height
                                                    '&:hover': {
                                                        backgroundColor: '#1e3040', // Slightly darker shade for hover
                                                    },
                                                    padding: '4px 12px',
                                                }}
                                                onClick={handleFlightTimeSave}
                                            >
                                                Done
                                            </Button>

                                            <Button
                                                variant="contained"
                                                size="small"
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    backgroundColor: '#f44336',
                                                    color: 'white',
                                                    height: '30px', // Reduced height
                                                    '&:hover': {
                                                        backgroundColor: '#d32f2f', // Slightly darker shade for hover
                                                    },
                                                    padding: '4px 12px',
                                                }}
                                                onClick={() => setIsEditingFlightTime(false)}
                                            >
                                                Cancel
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            ) : null}

                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            <span style={{ fontWeight: 500 }}>Takeoff Location:</span>&nbsp;&nbsp;{missionData?.jsonPlaceData?.preferredAddress || 'Unknown'}
                        </Typography>
                    </Box>

                    {hasCapability(subscription?.planId, CAPABILITIES.POST_FLIGHT) ? (
                        <>
                            {/* Maintenance Notes Input */}
                            <TextField
                                id="maintenance-notes"
                                name="maintNotes"
                                label="Maintenance Notes"
                                type="text"
                                multiline
                                minRows={3}
                                value={missionData?.maintNotes || ''}
                                onChange={handleFieldChange}
                                fullWidth
                                slotProps={{ input: { maxLength: 1000 } }}
                                helperText={
                                    missionData?.maintNotes
                                        ? `${missionData.maintNotes?.length}/500 characters used`
                                        : 'Provide maintenance notes for any required follow-up.'
                                }
                            />

                            {/* Flight Debrief Input */}
                            <TextField
                                id="flight-debrief"
                                name="flightDebrief"
                                label="Flight Debrief"
                                type="text"
                                multiline
                                minRows={3}
                                value={missionData?.flightDebrief || ''}
                                onChange={handleFieldChange}
                                fullWidth
                                slotProps={{ input: { maxLength: 1000 } }}
                                helperText={
                                    missionData?.flightDebrief
                                        ? `${missionData.flightDebrief?.length}/500 characters used`
                                        : 'Summarize the flight details and outcomes.'
                                }
                            />
                        </>
                    ) : null}
                </Box>
            </Collapse>
        </Box>
    );
};

export default PostflightSection;